import { useState, useEffect } from "react";
import type {
  Category,
  EventSubtask,
  RecurrenceRule,
  PlanEvent,
} from "../../../shared/types";
import { getToday } from "../../../shared/types/date-helpers";
import {
  setFormPreview,
  clearFormPreview,
} from "../../calendar/stores/form-preview-store";

export function useEventFormState(
  defaultCategory: Category,
  defaultDate?: string,
  defaultStartTime?: string,
  defaultEndTime?: string,
  eventToEdit?: PlanEvent,
) {
  const [title, setTitle] = useState(eventToEdit?.title ?? "");
  const [category, setCategory] = useState<Category>(
    eventToEdit?.category ?? defaultCategory,
  );
  const [date, setDate] = useState(
    eventToEdit?.date ?? defaultDate ?? getToday(),
  );
  const [startTime, setStartTime] = useState(
    eventToEdit?.startTime ?? defaultStartTime ?? "09:00",
  );
  const [endTime, setEndTime] = useState(
    eventToEdit?.endTime ?? defaultEndTime ?? "10:00",
  );
  const [description, setDescription] = useState(
    eventToEdit?.description ?? "",
  );
  const [notes, setNotes] = useState(eventToEdit?.notes ?? "");
  const [recurrence, setRecurrence] = useState<RecurrenceRule | null>(
    eventToEdit?.recurrence ?? null,
  );
  const [reminderMinutes, setReminderMinutes] = useState<number | null>(
    eventToEdit?.reminderMinutes ?? null,
  );
  const [reminderTime, setReminderTime] = useState<string | null>(
    eventToEdit?.reminderTime ?? null,
  );
  const [reminderType, setReminderType] = useState<"BEFORE" | "AT_TIME" | null>(
    eventToEdit?.reminderType ?? null,
  );
  const [allDay, setAllDay] = useState(eventToEdit?.allDay ?? false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([...(eventToEdit?.tags ?? [])]);
  const [eventSubtasks, setEventSubtasks] = useState<EventSubtask[]>(
    eventToEdit?.eventSubtasks ? [...eventToEdit.eventSubtasks] : [],
  );

  // Sync all visual fields to the calendar preview store
  useEffect(() => {
    setFormPreview({
      eventId: eventToEdit?.id ?? null,
      category,
      startTime,
      endTime,
      date,
      allDay,
      title,
    });
  }, [category, startTime, endTime, date, allDay, title, eventToEdit?.id]);

  useEffect(() => clearFormPreview, []);

  return {
    title,
    setTitle,
    category,
    setCategory,
    date,
    setDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    description,
    setDescription,
    notes,
    setNotes,
    recurrence,
    setRecurrence,
    reminderMinutes,
    setReminderMinutes,
    reminderTime,
    setReminderTime,
    reminderType,
    setReminderType,
    allDay,
    setAllDay,
    tagInput,
    setTagInput,
    tags,
    setTags,
    eventSubtasks,
    setEventSubtasks,
  };
}
